import { Component, inject, input, output, signal, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SettingsService } from '@app/_services/settings.service';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { MeoAttachment } from '@app/_interfaces/meo.interface';
import { processFileForMeo, MEO_ACCEPTED_FILE_TYPES } from '@app/_utils/meo-attachment.utils';
import { log } from '@app/_services/logger';

@Component({
  selector: 'app-meo-input',
  standalone: true,
  imports: [CommonModule, FormsModule, SvgComponent],
  templateUrl: './meo-input.component.html',
  styleUrl: './meo-input.component.scss',
})
export class MeoInputComponent {
  readonly ss = inject(SettingsService);

  /** Emitted when the user sends a text message (with optional attachment). */
  messageSent = output<string>();

  /** Emitted when user sends a message with an attachment. */
  messageWithAttachment = output<{ text: string; attachment: MeoAttachment }>();

  /** Emitted when the user requests to start/stop voice recording. */
  micPressed = output<void>();

  /** Whether a response is currently loading (bound from parent). */
  isLoading = input(false);

  /** Whether a Meo attachment upload is in progress (bound from parent). */
  isMeoUploading = input(false);

  /** Whether voice recording is active. */
  isRecording = signal(false);

  /** Whether voice input is supported in this browser. */
  isVoiceSupported = signal(typeof window !== 'undefined' && typeof MediaRecorder !== 'undefined');

  /** The current input text. */
  inputText = '';

  /** Currently attached file (max 1 at a time for Meo). */
  pendingAttachment = signal<MeoAttachment | null>(null);

  /** Processing state for attachment. */
  isProcessingFile = signal(false);

  /** Whether drag is hovering over the input area. */
  isDragOver = signal(false);

  /** Attachment error message key. */
  attachmentError = signal<string | null>(null);

  /** Accepted file types for the hidden input. */
  readonly acceptedTypes = MEO_ACCEPTED_FILE_TYPES;

  @ViewChild('textInput') textInputRef!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

  get canSend(): boolean {
    const hasText = this.inputText.trim().length > 0;
    const hasAttachment = this.pendingAttachment() !== null;
    return (hasText || hasAttachment) && !this.isLoading() && !this.isProcessingFile();
  }

  onSend(): void {
    if (!this.canSend) return;
    const text = this.inputText.trim();
    const attachment = this.pendingAttachment();

    this.inputText = '';
    this.pendingAttachment.set(null);
    this.attachmentError.set(null);

    if (attachment) {
      this.messageWithAttachment.emit({ text: text || attachment.fileName, attachment });
    } else {
      this.messageSent.emit(text);
    }

    // Reset textarea height
    if (this.textInputRef?.nativeElement) {
      this.textInputRef.nativeElement.style.height = 'auto';
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    // Send on Enter (without shift)
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSend();
    }
  }

  onInput(): void {
    // Auto-grow textarea
    if (this.textInputRef?.nativeElement) {
      const el = this.textInputRef.nativeElement;
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 120) + 'px';
    }
  }

  onMicPress(): void {
    this.micPressed.emit();
  }

  // ==================== Attachment Handling ====================

  onAttachClick(): void {
    if (this.isLoading() || this.isProcessingFile() || this.isMeoUploading()) return;
    this.fileInputRef?.nativeElement?.click();
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      await this.processFile(file);
    }
    // Reset input so the same file can be re-selected
    input.value = '';
  }

  removeAttachment(): void {
    this.pendingAttachment.set(null);
    this.attachmentError.set(null);
  }

  // ==================== Drag and Drop ====================

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);
  }

  async onDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);

    const file = event.dataTransfer?.files?.[0];
    if (file) {
      await this.processFile(file);
    }
  }

  // ==================== File Processing ====================

  private async processFile(file: File): Promise<void> {
    this.isProcessingFile.set(true);
    this.attachmentError.set(null);

    try {
      const { attachment, error } = await processFileForMeo(file);
      if (error) {
        this.attachmentError.set(error);
        log.warn('MeoInput', 'File rejected', { fileName: file.name, error });
      } else if (attachment) {
        this.pendingAttachment.set(attachment);
        log.info('MeoInput', 'File attached', { fileName: file.name, mimeType: attachment.mimeType });
      }
    } catch (err) {
      this.attachmentError.set('meo_error_file_processing');
      log.error('MeoInput', 'File processing error', err as Error);
    } finally {
      this.isProcessingFile.set(false);
    }
  }

  /** Helper: get a display-friendly file size. */
  getFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  /** Helper: determine if the attachment is an image (for thumbnail preview). */
  isImage(attachment: MeoAttachment): boolean {
    return attachment.mimeType.startsWith('image/');
  }
}
